import React from 'react';
import { useAmplifyAuth } from '@/shared/hooks/useAmplifyAuth';
import { useUserInfo } from '@/shared/hooks/useUserInfo';
import { useVariousFetch } from '@/shared/hooks/useVariousFetcher';
import type { InquiryApiRequest } from '../../api/route';
import type { RouteHandlerResponse } from 'u-next/custom';

type State = {
  pageStatus: 'INIT' | 'INPUT' | 'COMPLETE';
  errorMessage: string;
  isLoggedIn: boolean;
  formValues: InquiryApiRequest;
};

export type UseInquiry = {
  state: State;
  sendInquiry: (values: State['formValues']) => Promise<void>;
  isSending: boolean;
};

export function useInquiry(): UseInquiry {
  const [state, setState] = React.useState<State>({
    pageStatus: 'INIT',
    errorMessage: '',
    isLoggedIn: false,
    formValues: {
      inquiryType: 'start',
      detail: '',
      contactEmail: '',
      registeredEmail: '',
    },
  });
  const { isLoadedUserInfo, userInfo } = useUserInfo();
  const { currentAuthenticatedUser } = useAmplifyAuth();
  const { fetcher, loading } = useVariousFetch<RouteHandlerResponse>();

  const sendInquiry: UseInquiry['sendInquiry'] = React.useCallback(
    async (values) => {
      const apiData = await fetcher('/inquiry/api', {
        method: 'POST',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      setState((state) => ({
        ...state,
        pageStatus: apiData.result ? 'COMPLETE' : 'INPUT',
        errorMessage: apiData.errorMessage,
        formValues: values,
      }));
    },
    [fetcher]
  );

  React.useEffect(() => {
    (async () => {
      if (!isLoadedUserInfo) {
        return;
      }

      const userData = await currentAuthenticatedUser().then(
        (response) => response.data
      );

      setState((state) => ({
        ...state,
        pageStatus: 'INPUT',
        isLoggedIn: userInfo.isLoggedIn,
        formValues: {
          ...state.formValues,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          registeredEmail: (userData as any)?.attributes?.email ?? '', // 強引な取得
        },
      }));
    })();
  }, [currentAuthenticatedUser, isLoadedUserInfo, userInfo.isLoggedIn]);

  return {
    state,
    sendInquiry,
    isSending: loading,
  };
}
